import { validationIssue } from '@dinewise/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getCountries,
  parsePhoneNumberWithError as parse,
  type CountryCode,
} from 'libphonenumber-js';
import * as z from 'zod';
import { emailSchema } from './email.ts';
import { zfd } from './form-validation.ts';

export type { CountryCode };

const SUPPORTED_PHONE_COUNTRIES: ReadonlySet<string> = new Set(getCountries());

export const isPhoneCountryCode = (value: string): value is CountryCode =>
  value.length === 2 && SUPPORTED_PHONE_COUNTRIES.has(value.toUpperCase());

export const resolvePhoneCountryCode = (country: string | null | undefined): CountryCode => {
  if (country) {
    const upper = country.toUpperCase();
    if (isPhoneCountryCode(upper)) {
      return upper;
    }
  }

  return 'US';
};

export const UUID_REGEX =
  /^(?:[\da-f]{8}-[\da-f]{4}-[1-8][\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}|0{8}-(?:0{4}-){3}0{12})$/i;

export type UUID = string & { readonly _brand: 'UUID' };

export const isUUID = (value: unknown): value is UUID =>
  typeof value === 'string' && UUID_REGEX.test(value);

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

export const parseUUID = (value: string): UUID => {
  if (!isUUID(value)) {
    throw new Error(`Invalid UUID: ${value}`);
  }

  return value;
};

export const createPhoneDetailsSchema = (countryCode: CountryCode = 'US') =>
  z.string().transform((input, ctx) => {
    try {
      const parsed = parse(input, countryCode);

      if (!parsed.isValid()) {
        throw new Error('Invalid phone number');
      }

      return parsed;
    } catch {
      // Named rather than a literal message so a per-request error map can
      // localize it; a literal would short-circuit every map.
      ctx.addIssue({ code: 'custom', params: validationIssue('phone.invalid') });

      return z.NEVER;
    }
  });

export const phoneDetailsSchema = createPhoneDetailsSchema('US');

export const booleanSchema = z
  .union([z.boolean(), z.string()])
  .transform(val => (typeof val === 'boolean' ? val : val === 'true' || val === 'on'));

export const createPhoneNumberSchema = (countryCode: CountryCode = 'US') =>
  createPhoneDetailsSchema(countryCode).transform(phone => phone.number);

export const phoneNumberSchema = createPhoneNumberSchema('US');

const imageSourceSchema = z.object({
  src: z.string(),
  sizes: z.string().nullish(),
  srcSet: z.string().nullish(),
});

export const assignHostSchema = z.object({
  subaction: z.literal('assignHost'),
  firstName: zfd.text(z.string().min(1).max(50)),
  lastName: zfd.text(z.string().min(1).max(50)),
  hasAccount: z.boolean(),
  email: zfd.text(emailSchema),
  phoneNumber: zfd.text(phoneNumberSchema),
  profilePicture: imageSourceSchema.nullish(),
  hostId: z.string(),
  hostReceiveEmails: z.boolean().default(true),
});
export const assignHostResolver = zodResolver(assignHostSchema);

export const parsePhoneDetails = (phoneNumber?: string | null) => {
  const parsedPhone = phoneDetailsSchema.safeParse(phoneNumber);

  if (!parsedPhone.success) {
    return null;
  }

  const rawDigitsWithoutInternationalCode = parsedPhone.data.formatNational().replace(/\D/g, '');

  return {
    rawDigitsWithoutInternationalCode,
    country: parsedPhone.data.country,
    countryCallingCode: parsedPhone.data.countryCallingCode,
    nationalNumber: parsedPhone.data.nationalNumber,
    carrierCode: parsedPhone.data.carrierCode,
    ext: parsedPhone.data.ext,
  };
};

export const parsePhoneNumber = (
  phoneNumber?: string | null,
  defaultCountry: CountryCode = 'US',
) => {
  const parsedPhone = createPhoneNumberSchema(defaultCountry).safeParse(phoneNumber);

  return parsedPhone.success ? parsedPhone.data : null;
};

export const isPhoneNumberValidInAnyCountry = (phoneNumber: string): boolean =>
  getCountries().some(country => parsePhoneNumber(phoneNumber, country) !== null);

// Adyen KYC rejects unassigned numbers such as `+1 555 123 4567`, so anything that ends up on an
// Adyen legal entity has to be a real number rather than merely non-empty.
export const isValidPhoneNumber = (phoneNumber?: string | null): boolean =>
  parsePhoneNumber(phoneNumber) !== null;

// Adyen accepts a US national ID as either the last 4 digits or all 9 digits of the SSN.
export const isValidSsn = (ssn?: string | null): boolean => {
  const digits = (ssn ?? '').replace(/\D/g, '');

  return digits.length === 4 || digits.length === 9;
};

/**
 * Adyen rejects a legal entity whose individual date of birth falls outside 1900..today with error
 * 30_102 ("Date of birth is not between 1900 and now"), which otherwise only surfaces once an admin
 * runs provisioning — long after the applicant has left the form.
 *
 * `YYYY-MM-DD` is compared as a string against today rather than parsed, because `new Date(...)` on
 * a date-only value is UTC midnight and would read as "tomorrow" for anyone west of UTC.
 */
/** Earliest date of birth Adyen accepts, and the `min` for a date of birth input. */
export const DATE_OF_BIRTH_MIN = '1900-01-01';

/**
 * Local `YYYY-MM-DD`, not `toISOString()` — that is UTC, so for anyone west of UTC it reads as
 * tomorrow for part of the day and would let a future date of birth through.
 */
export const toIsoDateString = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

export const isValidDateOfBirth = (dob?: string | null, today = new Date()): boolean => {
  const value = (dob ?? '').trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  // Sliced rather than destructured off `split`, which types each part as possibly undefined even
  // though the pattern above already fixed the shape.
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));

  // Rejects a well-formed but non-existent date such as 1990-02-31, which Date would roll forward.
  const asDate = new Date(Date.UTC(year, month - 1, day));
  if (
    asDate.getUTCFullYear() !== year ||
    asDate.getUTCMonth() !== month - 1 ||
    asDate.getUTCDate() !== day
  ) {
    return false;
  }

  return value >= DATE_OF_BIRTH_MIN && value <= toIsoDateString(today);
};

// Adyen validates the ABA checksum on US routing numbers, so a 9-digit string is not enough:
// 3(d1+d4+d7) + 7(d2+d5+d8) + (d3+d6+d9) has to be a multiple of 10.
export const isValidRoutingNumber = (routingNumber?: string | null): boolean => {
  const digits = (routingNumber ?? '').replace(/\D/g, '');

  if (digits.length !== 9) {
    return false;
  }

  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let checksum = 0;

  for (const [index, weight] of weights.entries()) {
    checksum += weight * Number(digits[index]);
  }

  return checksum % 10 === 0;
};

/**
 * Adyen makes the business-line web address mandatory for ecommerce sales channels, and every venue
 * is provisioned with `eCommerce`, so a website is required to pass verification. A bare host is
 * accepted because provisioning adds the scheme before calling Adyen.
 */
export const isValidWebsite = (input?: string | null): boolean => {
  const trimmed = (input ?? '').trim();

  if (trimmed === '') {
    return false;
  }

  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    return new URL(candidate).hostname.includes('.');
  } catch {
    return false;
  }
};

// Formats a stored phone number for display: US numbers show the country code then
// their familiar national format, others show the international format.
// Unparseable values are returned as-is so display never breaks.
export const formatPhoneNumberForDisplay = (phoneNumber?: string | null): string => {
  if (!phoneNumber) {
    return '';
  }

  const parsed = phoneDetailsSchema.safeParse(phoneNumber);
  if (!parsed.success) {
    return phoneNumber;
  }

  return parsed.data.country === 'US'
    ? `+${parsed.data.countryCallingCode} ${parsed.data.formatNational()}`
    : parsed.data.formatInternational();
};

export const jsonSchema = z
  .string()
  .refine(value => {
    try {
      JSON.parse(value);

      return true;
    } catch {
      return false;
    }
  })
  .transform(value => JSON.parse(value));

/**
 * A helper function to be used in switch statements to help create exhaustive checks with TS.
 * Using this as the "default" case when switching on a union type will cause a compile error,
 * which ensures that all cases are explicitly handled.
 *
 * @example
 * type MyUnionType = 'foo' | 'bar' | 'baz';
 *
 * const myUnion: MyUnionType = 'baz';
 *
 * switch (myUnion) {
 *   case 'foo':
 *     // do something
 *     break;
 *   case 'bar':
 *     // do something else
 *     break;
 *   default:
 *     exhaustiveMatchingGuard(myUnion); // TS will complain here, since 'baz' is not handled
 * }
 *
 * @param errorMsg - An optional error message to display if the value is not exhaustive.
 * @returns A never value, which will cause a compile error
 */
export const exhaustiveMatchingGuard = (_: never, errorMsg?: string): never => {
  throw new Error(errorMsg ?? 'Invalid match');
};

type ParseSearchParams = <
  T extends z.ZodObject<
    Record<
      string,
      | z.ZodString
      | z.ZodNumber
      | z.ZodBoolean
      | z.ZodArray<z.ZodString | z.ZodNumber | z.ZodBoolean>
    >,
    // oxlint-disable-next-line no-explicit-any Allow any type for the schema
    any
  >,
>(
  request: Pick<Request, 'url'> | URL,
  schema: T,
  onError?: (error: z.ZodError) => void,
) => Partial<z.infer<typeof schema>>;

const searchNumberSchema = z.string().transform(val => {
  if (Number.isNaN(Number(val))) {
    // Let string errors bubble up
    return val;
  }

  return Number(val);
});

const searchBooleanSchema = z.string().transform((val, ctx) => {
  if (val !== 'true' && val !== 'false') {
    ctx.addIssue({ code: 'custom', message: 'Not a boolean value' });

    return z.NEVER;
  }

  return val === 'true';
});

const searchArraySchema = z.string().transform(val => val.split(','));

/**
 * Parses search parameters from a request URL according to a provided Zod schema.
 *
 * This function is intended to help safely extract and type-check query parameters from a URL,
 * using a Zod object schema to define the expected types. It will try to transform the value into the expected input type.
 * It supports primitives and all values returned are partials.
 * Array types (of string, number, or boolean). If parsing fails, an optional error handler
 * can be called with the ZodError.
 *
 * @template T - A Zod object schema describing the expected search parameters.
 * @param {Pick<Request, 'url'>} request - The request object containing the URL to parse.
 * @param {T} schema - The Zod object schema to validate and transform the search parameters.
 * @param {(error: z.ZodError) => void} [onError] - Optional callback invoked if validation fails.
 * @returns {Partial<z.infer<T>>} An object containing the parsed and validated search parameters.
 */

export const parseSearchParams: ParseSearchParams = (request, schema, onError) => {
  const url = request instanceof URL ? request : new URL(request.url);

  const params: Record<string, unknown> = {};
  const rootSchema = schema.shape[0]?._zod.def;
  type SchemaDef = typeof rootSchema;

  const parseValue = (val: unknown, currSchema: SchemaDef): unknown => {
    if (!currSchema) {
      return undefined;
    }

    if (currSchema.type === 'string') {
      return typeof val === 'string' && val.length > 0 ? val : undefined;
    }

    if (!val) {
      return undefined;
    }

    if (currSchema.type === 'number') {
      const parsed = searchNumberSchema.safeParse(val);

      return parsed.success ? parsed.data : undefined;
    }
    if (currSchema.type === 'boolean') {
      const parsed = searchBooleanSchema.safeParse(val);

      return parsed.success ? parsed.data : undefined;
    }
    if (currSchema.type === 'array') {
      const parsed = searchArraySchema.safeParse(val);

      const data = parsed.success ? parsed.data : [];

      return data.map(item => parseValue(item, currSchema.element.def));
    }

    return undefined;
  };

  for (const [key, value] of url.searchParams.entries()) {
    params[key] = value;

    const schemaDef = schema.shape[key]?._zod.def;

    params[key] = parseValue(value, schemaDef);
  }

  type PartialOutput = z.ZodSafeParseResult<Partial<z.infer<typeof schema>>>;
  const parsed = schema.partial().safeParse(params) as PartialOutput;

  if (!parsed.success && onError) {
    onError(parsed.error);
  }

  return parsed.success ? parsed.data : ({} as Partial<z.infer<typeof schema>>);
};
